import { motion } from "framer-motion";
import { films } from "../../data/portfolio";

/* ---------- Feature film ---------- */

const FeatureRow = ({ film }) => {
  const [hero, ...rest] = film.gallery || [];

  return (
    <article data-testid={`film-card-${film.index}`} className="w-full">
      {/* Poster — huge, centered, dominant */}
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
        className="flex flex-col items-center"
      >
        <p
          className="text-[10px] uppercase tracking-[0.4em] mb-8"
          style={{ color: "var(--accent-amber)" }}
        >
          {film.index}  ·  Feature Film
        </p>

        <div className="relative w-full flex justify-center">
          <div
            className="absolute inset-0 -m-12 pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(200,162,91,0.16), transparent 65%)",
            }}
          />
          <div className="relative w-full max-w-[560px] aspect-[2/3] overflow-hidden bg-neutral-900 shadow-[0_40px_120px_rgba(0,0,0,0.6)]">
            <img
              src={film.poster}
              alt={`${film.title} — official film poster directed by Valeriy Rudenko`}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.19,1,0.22,1)] hover:scale-[1.02]"
            />
          </div>
        </div>

        <h3 className="mt-14 sm:mt-16 font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-none tracking-tighter text-white text-center">
          <span className="italic">{film.title}</span>
        </h3>

        <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-white/45">
          A film by Valeriy Rudenko
        </p>
      </motion.div>

      {/* Gallery — cinema catalogue rhythm */}
      {film.gallery && film.gallery.length > 0 && (
        <div className="mt-32 sm:mt-40 lg:mt-48 space-y-24 sm:space-y-32 lg:space-y-40">
          {/* Hero still — natural aspect ratio preserves the original frame exactly */}
          {hero && (
            <motion.figure
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 1.3, ease: [0.19, 1, 0.22, 1] }}
              className="w-full max-w-[1200px] mx-auto"
            >
              <div className="relative w-full overflow-hidden bg-neutral-900 group">
                <img
                  src={hero}
                  alt={`${film.title} — cinematic still 01, directed by Valeriy Rudenko`}
                  data-testid={`surprise-still-1`}
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto transition-transform duration-[2000ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.02]"
                />
              </div>
              <figcaption className="mt-4 flex items-baseline justify-between text-[10px] uppercase tracking-[0.35em] text-white/40">
                <span>Still 01 / {film.gallery.length.toString().padStart(2, "0")}</span>
                <span style={{ color: "var(--accent-amber)" }}>{film.title}</span>
              </figcaption>
            </motion.figure>
          )}

          {/* Remaining stills — natural aspect ratio, staggered pairs */}
          {rest.length > 0 && (
            <div className="space-y-24 sm:space-y-32">
              {Array.from({ length: Math.ceil(rest.length / 2) }).map((_, rowIdx) => {
                const pair = rest.slice(rowIdx * 2, rowIdx * 2 + 2);
                return (
                  <div
                    key={rowIdx}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 lg:gap-14 items-start"
                  >
                    {pair.map((src, i) => {
                      const stillNumber = 2 + rowIdx * 2 + i;
                      return (
                        <motion.figure
                          key={i}
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-120px" }}
                          transition={{
                            duration: 1.2,
                            delay: i * 0.1,
                            ease: [0.19, 1, 0.22, 1],
                          }}
                          className={i === 1 ? "md:mt-16 lg:mt-24" : ""}
                        >
                          <div className="relative w-full overflow-hidden bg-neutral-900 group">
                            <img
                              src={src}
                              alt={`${film.title} — cinematic still ${stillNumber}, directed by Valeriy Rudenko`}
                              data-testid={`surprise-still-${stillNumber}`}
                              loading="lazy"
                              decoding="async"
                              className="block w-full h-auto cinema-grade"
                            />
                          </div>
                          <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/35">
                            Still {stillNumber.toString().padStart(2, "0")} /{" "}
                            {film.gallery.length.toString().padStart(2, "0")}
                          </figcaption>
                        </motion.figure>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Watch links — centered, editorial focus */}
      <div className="mt-32 sm:mt-40 lg:mt-48">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="flex flex-col items-center text-center"
        >
          <div
            className="h-px w-16 mb-8"
            style={{ backgroundColor: "var(--accent-amber)" }}
          />
          <p
            className="text-[10px] uppercase tracking-[0.45em] mb-4"
            style={{ color: "var(--accent-amber)" }}
          >
            Now Streaming
          </p>
          <h4 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.1] tracking-tight text-white max-w-xl">
            <span className="block">Watch</span>
            <span className="italic block mt-1">&ldquo;{film.title}&rdquo;</span>
          </h4>

          {/* Primary YouTube CTA — plain HTML anchor, opens in a new tab.
              No onClick handler, no router intercept, no window.open — the browser
              handles the navigation natively via target="_blank". */}
          {film.youtubeUrl && (
            <a
              href={film.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="watch-primary-youtube"
              className="group mt-10 sm:mt-12 inline-flex items-center gap-4 px-9 sm:px-10 py-5 sm:py-6 text-[12px] uppercase tracking-[0.32em] text-black transition-all duration-500 hover:-translate-y-0.5"
              style={{
                backgroundColor: "var(--accent-amber)",
                border: "1px solid var(--accent-amber)",
                boxShadow: "0 20px 60px rgba(200,162,91,0.18)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 24px 80px rgba(200,162,91,0.32)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 20px 60px rgba(200,162,91,0.18)";
              }}
            >
              <span className="inline-block w-0 h-0 border-y-[7px] border-y-transparent border-l-[11px] border-l-black" />
              Watch &ldquo;{film.title}&rdquo;
            </a>
          )}

          <p className="mt-8 sm:mt-10 text-[13px] sm:text-sm text-white/55 font-sans tracking-wide">
            Available on the platforms below
          </p>

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-4 sm:gap-6 w-full max-w-2xl">
            {(film.watchLinks || [])
              .filter((w) => w.url)
              .map((w, i) => (
                <a
                  key={i}
                  href={w.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`watch-link-${w.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex-1 inline-flex items-center justify-between gap-6 border px-8 py-5 text-[11px] uppercase tracking-[0.28em] text-white transition-colors duration-500 hover:text-black"
                  style={{ borderColor: "rgba(200,162,91,0.55)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--accent-amber)";
                    e.currentTarget.style.borderColor = "var(--accent-amber)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor = "rgba(200,162,91,0.55)";
                  }}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className="w-1.5 h-1.5 rounded-full transition-colors duration-500 group-hover:bg-black"
                      style={{ backgroundColor: "var(--accent-amber)" }}
                    />
                    {w.label}
                  </span>
                  <span className="transition-transform duration-500 group-hover:translate-x-1">
                    ↗
                  </span>
                </a>
              ))}
          </div>
        </motion.div>
      </div>
    </article>
  );
};

/* ---------- Short Films — cinematic staggered gallery, one still per film ---------- */

const ShortsRow = ({ film }) => {
  const stills = film.stills || [];

  return (
    <article data-testid={`film-card-${film.index}`} className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
        className="flex flex-col items-center mb-20 sm:mb-28 lg:mb-32"
      >
        <p
          className="text-[10px] uppercase tracking-[0.4em] mb-8"
          style={{ color: "var(--accent-amber)" }}
        >
          {film.index}  ·  Short Films
        </p>
        <h3 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-none tracking-tighter text-white text-center">
          <span className="italic">{film.title}</span>
        </h3>
        <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-white/40">
          {stills.length.toString().padStart(2, "0")} Films  ·  {stills.length.toString().padStart(2, "0")} Stills
        </p>
      </motion.div>

      {/* Staggered gallery — natural aspect ratio preserves each frame's full composition */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start">
        {stills.map((item, i) => {
          const src = typeof item === "string" ? item : item.url;
          const shortTitle = typeof item === "string" ? null : item.title;
          const titleLines = typeof item === "string" ? null : item.titleLines;
          const shortLink = typeof item === "string" ? null : item.link;
          const isOffset = i % 2 === 1;
          const label = `Short ${(i + 1).toString().padStart(2, "0")}`;
          const isMultiline = Array.isArray(titleLines) && titleLines.length > 1;
          const linkProps = shortLink
            ? {
                href: shortLink,
                target: "_blank",
                rel: "noopener noreferrer",
                "data-testid": `short-link-${i + 1}`,
              }
            : {};
          const Wrapper = shortLink ? "a" : "div";
          return (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{
                duration: 1.2,
                delay: (i % 2) * 0.12,
                ease: [0.19, 1, 0.22, 1],
              }}
              className={isOffset ? "md:mt-24 lg:mt-40" : ""}
            >
              <Wrapper
                {...linkProps}
                className={`group block ${shortLink ? "cursor-pointer" : ""}`}
              >
                <div className="relative w-full overflow-hidden bg-neutral-900">
                  <img
                    src={src}
                    alt={`${shortTitle || film.title} — short film by Valeriy Rudenko, ${label}`}
                    data-testid={`short-still-${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto cinema-grade"
                  />
                  {/* Subtle darken on hover for legibility of the watch chip */}
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 55%, rgba(5,5,5,0.55) 100%)",
                    }}
                  />
                  {/* "Watch" hover chip — appears from the bottom edge (no arrow, understated) */}
                  {shortLink && (
                    <span
                      aria-hidden
                      className="absolute bottom-4 right-4 sm:bottom-5 sm:right-5 inline-flex items-center px-3.5 py-1.5 text-[10px] uppercase tracking-[0.42em] text-white/95 border border-white/45 backdrop-blur-sm bg-black/35 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]"
                    >
                      Watch
                    </span>
                  )}
                </div>
                <figcaption
                  className={`mt-6 sm:mt-7 flex ${
                    isMultiline ? "items-start" : "items-baseline"
                  } gap-6 sm:gap-8 justify-between`}
                >
                  <p
                    className="text-[10px] uppercase tracking-[0.4em] text-white/50 shrink-0 whitespace-nowrap transition-colors duration-500 group-hover:text-white/75"
                    data-testid={`short-label-${i + 1}`}
                  >
                    {label.toUpperCase()}
                  </p>
                  <h4
                    className={`font-serif text-[17px] sm:text-[19px] lg:text-[20px] font-normal leading-[1.2] tracking-[0.005em] text-white transition-colors duration-500 group-hover:text-[color:var(--accent-amber)] ${
                      isMultiline ? "text-center" : "whitespace-nowrap text-right"
                    }`}
                    data-testid={`short-title-${i + 1}`}
                  >
                    <span
                      className={`italic ${
                        shortLink
                          ? "underline-offset-4 decoration-transparent group-hover:decoration-[color:var(--accent-amber)] group-hover:underline"
                          : ""
                      }`}
                    >
                      {isMultiline
                        ? titleLines.map((line, idx) => (
                            <span key={idx} className="block">
                              {line}
                            </span>
                          ))
                        : shortTitle || "Untitled"}
                    </span>
                  </h4>
                </figcaption>
              </Wrapper>
            </motion.figure>
          );
        })}
      </div>
    </article>
  );
};

/* ---------- Section ---------- */

export const SelectedFilms = () => {
  return (
    <section
      id="films"
      data-testid="films-section"
      className="relative py-40 sm:py-56 lg:py-64 px-6 sm:px-12 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
          className="mb-32 sm:mb-40 lg:mb-48 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-6">
            <span style={{ color: "var(--accent-amber)" }}>02</span> — Selected Films
          </p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tighter text-white max-w-4xl mx-auto">
            A body of work,
            <br />
            <span className="italic opacity-70">told in fragments.</span>
          </h2>
        </motion.div>

        <div className="space-y-48 sm:space-y-56 lg:space-y-64">
          {films.map((film) =>
            film.kind === "feature" ? (
              <FeatureRow key={film.id} film={film} />
            ) : (
              <ShortsRow key={film.id} film={film} />
            )
          )}
        </div>
      </div>
    </section>
  );
};
